import { useState, useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import { experimentalStyled as styled } from '@material-ui/core/styles';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Box } from '@material-ui/core';
import WarningIcon from '@material-ui/icons/Warning';
import DashboardNavbar from './DashboardNavbar';
import DashboardSidebar from './DashboardSidebar';
import useAuth from '../../hooks/useAuth';
import axios from '../../utils/axios';
import { differenceInDays, format } from 'date-fns';

// ----------------------------------------------------------------------

const APP_BAR_MOBILE = 64;
const APP_BAR_DESKTOP = 92;

const RootStyle = styled('div')({
  display: 'flex',
  minHeight: '100%',
  overflow: 'hidden'
});

const MainStyle = styled('div')(({ theme }) => ({
  flexGrow: 1,
  overflow: 'auto',
  minHeight: '100%',
  paddingTop: APP_BAR_MOBILE + 24,
  paddingBottom: theme.spacing(10),
  [theme.breakpoints.up('lg')]: {
    paddingTop: APP_BAR_DESKTOP + 24,
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2)
  }
}));

// ----------------------------------------------------------------------

export default function DashboardLayout() {
  const [open, setOpen] = useState(false);
  const [expiryNotifOpen, setExpiryNotifOpen] = useState(false);
  const [expiryDetails, setExpiryDetails] = useState(null);
  const [expiringStaffList, setExpiringStaffList] = useState([]);
  const { user } = useAuth();

  useEffect(() => {
    if (window.innerWidth >= 1200) {
      setOpen(true);
    }
  }, []);

  useEffect(() => {
    const checkExpiries = async () => {
      if (sessionStorage.getItem('emiratesExpiryNotified')) return;

      let hasNotice = false;

      // 1. Check personal expiry
      if (user?.emiratesIdExpiryDate) {
        const expiryDate = new Date(user.emiratesIdExpiryDate);
        const daysLeft = differenceInDays(expiryDate, new Date());
        
        if (daysLeft <= 30 && daysLeft >= 0) {
          setExpiryDetails({
            date: format(expiryDate, 'dd MMM yyyy'),
            days: daysLeft
          });
          hasNotice = true;
        }
      }

      // 2. Check staff expiries if Admin
      if (user?.role === 'Administrator' || user?.role === 'Super Administrator') {
        try {
          const res = await axios.get('/api/staffs/expiring');
          if (res.data && res.data.length > 0) {
            setExpiringStaffList(res.data);
            hasNotice = true;
          }
        } catch (err) {
          console.error('Error fetching expiring staff IDs', err);
        }
      }

      if (hasNotice) {
        setExpiryNotifOpen(true);
        sessionStorage.setItem('emiratesExpiryNotified', 'true');
      }
    };

    if (user) {
      checkExpiries();
    }
  }, [user]);

  return (
    <RootStyle>
      <DashboardNavbar isSidebarOpen={open} onOpenSidebar={() => setOpen(true)} />
      <DashboardSidebar isOpenSidebar={open} onOpenSidebar={() => setOpen(true)} onCloseSidebar={() => setOpen(false)} />
      <MainStyle>
        <Outlet />
      </MainStyle>

      <Dialog 
        open={expiryNotifOpen} 
        onClose={() => setExpiryNotifOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          style: {
            borderRadius: 16,
            padding: '8px',
            textAlign: 'center'
          }
        }}
      >
        <Box display="flex" justifyContent="center" mt={3} mb={1}>
          <Box
            style={{
              backgroundColor: 'rgba(255, 72, 66, 0.1)',
              color: '#FF4842',
              borderRadius: '50%',
              padding: '16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <WarningIcon style={{ fontSize: 48 }} />
          </Box>
        </Box>
        <DialogTitle style={{ paddingBottom: 0 }}>
          <Typography variant="h5" style={{ fontWeight: 700, color: '#212B36' }}>
            Action Required
          </Typography>
        </DialogTitle>
        <DialogContent>
          
          {/* Personal Notice */}
          {expiryDetails && (
            <Box mb={expiringStaffList.length > 0 ? 3 : 1}>
              <Typography variant="body1" style={{ color: '#637381', marginTop: 8, marginBottom: 24 }}>
                Your Emirates ID is nearing its expiry date. Please ensure it is renewed on time to avoid service interruptions.
              </Typography>
              
              <Box
                style={{
                  backgroundColor: '#F4F6F8',
                  borderRadius: 12,
                  padding: '16px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px'
                }}
              >
                <Typography variant="subtitle2" style={{ color: '#919EAB', textTransform: 'uppercase', letterSpacing: 1.1 }}>
                  Your Expiry Date
                </Typography>
                <Typography variant="h4" style={{ color: '#212B36', fontWeight: 700 }}>
                  {expiryDetails.date}
                </Typography>
                <Typography variant="subtitle1" style={{ color: '#FF4842', fontWeight: 600 }}>
                  {expiryDetails.days === 0 
                    ? 'Expires Today!' 
                    : `Expires in ${expiryDetails.days} day(s)`}
                </Typography>
              </Box>
            </Box>
          )}

          {/* Admin List */}
          {expiringStaffList.length > 0 && (
            <Box textAlign="left">
              <Typography variant="h6" style={{ color: '#212B36', fontWeight: 700, marginBottom: 16 }}>
                Staff Expiring IDs
              </Typography>
              <Typography variant="body2" style={{ color: '#637381', marginBottom: 16 }}>
                The following staff members have Emirates IDs that are expiring soon or have already expired.
              </Typography>
              
              <Box style={{ maxHeight: 300, overflow: 'auto', border: '1px solid #DFE3E8', borderRadius: 8 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#F4F6F8', borderBottom: '1px solid #DFE3E8' }}>
                      <th style={{ padding: '12px', fontSize: 14, color: '#637381' }}>Name</th>
                      <th style={{ padding: '12px', fontSize: 14, color: '#637381' }}>Emirates ID</th>
                      <th style={{ padding: '12px', fontSize: 14, color: '#637381' }}>Expiry Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {expiringStaffList.map(staff => {
                      const stExpiry = new Date(staff.emiratesIdExpiryDate);
                      const stDays = differenceInDays(stExpiry, new Date());
                      const isExpired = stDays < 0;

                      return (
                        <tr key={staff.id} style={{ borderBottom: '1px solid #DFE3E8' }}>
                          <td style={{ padding: '12px', fontSize: 14, fontWeight: 600, color: '#212B36' }}>
                            {staff.name}
                          </td>
                          <td style={{ padding: '12px', fontSize: 14, color: '#637381' }}>
                            {staff.emiratesIdNumber || 'N/A'}
                          </td>
                          <td style={{ padding: '12px', fontSize: 14, color: isExpired ? '#B72136' : '#FF4842', fontWeight: 600 }}>
                            {format(stExpiry, 'dd MMM yyyy')}
                            <div style={{ fontSize: 12, fontWeight: 400, marginTop: 4 }}>
                              {isExpired ? 'Expired' : `${stDays} day(s)`}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </Box>
            </Box>
          )}

        </DialogContent>
        <DialogActions style={{ justifyContent: 'center', paddingBottom: 24, paddingTop: 16 }}>
          <Button 
            onClick={() => setExpiryNotifOpen(false)} 
            variant="contained" 
            size="large"
            style={{ 
              backgroundColor: '#FF4842', 
              color: '#fff',
              boxShadow: '0 8px 16px 0 rgba(255, 72, 66, 0.24)',
              padding: '8px 32px',
              borderRadius: 8,
              fontWeight: 700,
              textTransform: 'none'
            }}
          >
            I Understand
          </Button>
        </DialogActions>
      </Dialog>
    </RootStyle>
  );
}
